import axios from "axios";
import { ReqMethods, ResponseStatus } from "../enums";
import env from "../config";

const BASE_URL = `${env.API_URL}`;

// The message the backend sends when a find/findOne matches nothing.
const NO_DATA = "no data found";

// Unwraps the standard { success, message, data } envelope.
// Never throws — callers branch on `error` instead of wrapping every call
// in try/catch.
function useServiceHandler() {
  return (fn) =>
    async (method, path, options = {}) => {
      try {
        const res = await fn(method, path, options);
        const body = res.data;
        const response =
          body && typeof body === "object" && "success" in body && "data" in body
            ? body.data
            : body;
        return { response, meta: body };
      } catch (err) {
        if (err.response && err.response.status === ResponseStatus.UNAUTHORIZED) {
          return { error: err.response?.data || err };
        }
        return { error: err.response ? err.response.data : err };
      }
    };
}

function useCallService() {
  const serviceHandler = useServiceHandler();

  const CallService = (method, path, { query, payload, headers = {} } = {}) => {
    const url = query ? `${BASE_URL}${path}?${query}` : `${BASE_URL}${path}`;

    const config = { headers };

    if (method === ReqMethods.GET || method === ReqMethods.DELETE)
      return axios[method](url, config);
    return axios[method](url, payload, config);
  };

  return serviceHandler(CallService);
}

/**
 * Every backend call in the app goes through here, grouped by the router that
 * serves it (backend/src/routes/*). Each method resolves to
 * `{ response, meta }` on success or `{ error }` on failure.
 */
function useAppServices() {
  const { GET, POST, PUT, DELETE } = ReqMethods;
  const CallService = useCallService();

  const apps = {
    // GET /apps — every query key is forwarded to AppsModel.find().
    // Returns [] rather than an error when the backend reports "no data found",
    // so list screens can render an empty state instead of a failure.
    Get: async (query = "") => {
      const res = await CallService(GET, "/apps", { query });
      // The backend answers an empty collection with 400 "no data found".
      // Only that exact case is an empty list — a 500 must stay an error.
      if (res.error && res.error?.message === NO_DATA) return { response: [] };
      return res;
    },

    // GET /apps/filter — a single document via findOne().
    Filter: (query = "") => CallService(GET, "/apps/filter", { query }),

    // PUT /apps — payload must carry _id; the rest is $set.
    Update: (payload) => CallService(PUT, "/apps", { payload }),

    Create: (payload) => CallService(POST, "/apps", { payload }),

    // DELETE /apps?_id=...
    Delete: (_id) => CallService(DELETE, "/apps", { query: `_id=${_id}` }),
  };

  const auth = {
    Login: (payload) => CallService(POST, "/auth/login", { payload }),
    Register: (payload) => CallService(POST, "/auth", { payload }),
    /**
     * POST /auth/integrate_app   { code, appId }
     *
     * Completes a marketplace install: the backend swaps the one-time code for
     * tokens using the app's saved client credentials and writes the install.
     * Resolves to { appId, appName, companyId, locationId, userType,
     * installStatus } — never the tokens themselves.
     */
    IntegrateApp: (payload) => CallService(POST, "/auth/integrate_app", { payload }),

    /**
     * POST /auth/sso/decrypt   { appId, key }
     *
     * Decodes the encrypted user context the GHL parent frame hands a custom
     * page. The SSO key lives on the app record server-side, so this cannot be
     * done in the browser.
     */
    SsoDecrypt: (payload) => CallService(POST, "/auth/sso/decrypt", { payload }),
  };

  const ghl = {
    // POST /services/ghl — generic passthrough to the GHL REST API.
    Call: (payload) => CallService(POST, "/services/ghl", { payload }),
  };

  /**
   * The install record for one account. `GET /app-installs/status` is the gate
   * for the whole public app page: it answers whether this appId + locationId
   * pair actually installed the app, and returns the app itself so the page
   * needs only one round trip. Tokens and OAuth secrets are stripped server-side.
   */
  const installs = {
    Status: (query = "") => CallService(GET, "/app-installs/status", { query }),
    GetCredentials: (query = "") =>
      CallService(GET, "/app-installs/credentials", { query }),
    SaveCredentials: (payload) =>
      CallService(POST, "/app-installs/credentials", { payload }),

    /**
     * POST /app-installs/webhook-confirmed   { appId, locationId, companyId, confirmed }
     *
     * The account ticking the webhook step off their setup checklist. We have no
     * way to read their provider account, so registration is self-reported until
     * a real inbound message proves it.
     */
    SetWebhookConfirmed: (payload) =>
      CallService(POST, "/app-installs/webhook-confirmed", { payload }),
  };

  /**
   * Feature requests from this account. The backend checks the appId + account
   * pair is a live install before reading or writing anything.
   */
  const featureRequests = {
    // GET /feature-requests?appId=&locationId=|companyId= — this account's own.
    List: (query = "") => CallService(GET, "/feature-requests", { query }),
    // POST /feature-requests  { appId, locationId, companyId, category, title,
    //                           description, submittedBy }
    Create: (payload) => CallService(POST, "/feature-requests", { payload }),
  };

  /**
   * Support tickets for this account. Every call carries the account (appId +
   * locationId/companyId); the backend checks it is a live install and that
   * the ticket belongs to it.
   */
  const support = {
    List: (query = "") => CallService(GET, "/support/tickets", { query }),
    Get: (id, query = "") => CallService(GET, `/support/tickets/${id}`, { query }),
    // { appId, locationId, companyId, subject, category, message, submittedBy }
    Create: (payload) => CallService(POST, "/support/tickets", { payload }),
    // { appId, locationId, companyId, message, authorName }
    Reply: (id, payload) => CallService(POST, `/support/tickets/${id}/messages`, { payload }),
    Read: (id, payload) => CallService(POST, `/support/tickets/${id}/read`, { payload }),
  };

  // Live announcements for the notification bar. Public, read-only.
  const announcements = {
    Active: (appId) => CallService(GET, "/announcements/active", { query: `appId=${encodeURIComponent(appId || "")}` }),
  };

  // Notifications sent to this account. The backend checks it is a live
  // install and only returns what was addressed to it.
  const notifications = {
    List: (query = "") => CallService(GET, "/notifications", { query }),
    // id, or "all"
    Read: (id, payload) => CallService(POST, `/notifications/${id}/read`, { payload }),
  };

  return { apps, auth, ghl, installs, featureRequests, support, announcements, notifications };
}

export { useAppServices, useCallService, NO_DATA };
export default useAppServices;
